import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useResolvedAppTestProduct } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useResolvedAppTestProduct'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionEntity } from '@context/EntityContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { filterDocumentsByClient } from '@utils/documents'
import { filterDuplicatesById } from '@utils/duplicates'
import { BasePaths } from '@utils/path'

import { LegalDocumentDto } from '@model/CompanyLegalDocumentDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { Loading } from '@shared/components/Loading/Loading'

import { LegalDocumentsForm, LegalDocumentsValues } from './LegalDocumentsForm'
import { useDocumentMarketDataIdState } from './ReadDocumentMarketDataPage'

const useProductLegalDocumentsFetch = (props?: ProductLegalDocumentsProps) => {
  const apiClient = useApiClient(ClientApiClient)
  const entity = useSessionEntity()
  const locale = useSessionLanguage()

  const { account } = useAccountReadContext()
  const resolvedProductId = useResolvedAppTestProduct()

  const productIds = [resolvedProductId]

  const { data: documents = [], isLoading } = useQuery({
    queryKey: queryKeys.productRegistration.legalDocuments(resolvedProductId, locale),
    queryFn: () =>
      props?.documents
        ? Promise.resolve(props.documents)
        : apiClient.getLegalDocumentsQuery(locale, entity, productIds),
  })

  const accountAgreedDocuments = new Set(account?.agreedLegalDocuments.map((doc) => doc.id))

  const docs = documents
    .filter(filterDocumentsByClient)
    .filter(filterDuplicatesById)
    .filter((doc) => !accountAgreedDocuments.has(doc.id))
    .sort((a, b) => a.order - b.order)

  return { data: docs, isLoading }
}

export const useProductLegalDocumentsMutate = () => {
  const apiClient = useApiClient(ClientApiClient)
  const queryClient = useQueryClient()

  const [isLoading, setLoading] = useState(false)
  const [isSuccess, setSuccess] = useState(false)

  const mutate = async (values: LegalDocumentsValues, callback?: () => Promise<void>) => {
    try {
      setLoading(true)

      await apiClient.updateClientLegalDocuments({
        agreedLegalDocuments: values.agreedLegalDocuments,
      })

      await queryClient.invalidateQueries({ queryKey: queryKeys.document.list() })
      setSuccess(true)
      await callback?.()
    } catch {
      setLoading(false)
    } finally {
      setLoading(false)
    }
  }

  return { mutate, isLoading, isSuccess }
}

interface ProductLegalDocumentsProps {
  documents?: LegalDocumentDto[]
  productId?: TickmillProductType
  onSubmit?(values: LegalDocumentsValues): void | Promise<void>
}

export const useProductLegalDocumentsStepPage = (props?: ProductLegalDocumentsProps) => {
  const { marketDataClassificationId } = useDocumentMarketDataIdState()

  const documents = useProductLegalDocumentsFetch(props)
  const mutation = useProductLegalDocumentsMutate()

  const handleSubmit = async (values: LegalDocumentsValues) => {
    if (props?.onSubmit) {
      await props.onSubmit(values)
    }
  }

  return { marketDataClassificationId, documents, mutation, handleSubmit }
}

export const ProductLegalDocumentsPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const locale = useSessionLanguage()
  const resolvedProductId = useResolvedAppTestProduct()
  const isFuturesProduct = resolvedProductId === TickmillProductType.ETD

  const { refreshAccount } = useAccountWriteContext()

  const { documents, mutation, marketDataClassificationId } = useProductLegalDocumentsStepPage()

  const handleSubmit = async (values: LegalDocumentsValues) => {
    await mutation.mutate(values, async () => {
      await refreshAccount(locale)

      navigate(BasePaths.TakeAppTest)
    })
  }

  return (
    <Loading isLoading={documents.isLoading}>
      <LegalDocumentsForm
        title={isFuturesProduct ? t('Sign up.Registration Futures Trading') : t('Sign up.Legal')}
        documents={documents.data}
        groupReferences={groupReferences(documents.data)}
        marketDataClassificationId={marketDataClassificationId}
        onSubmit={handleSubmit}
      />
    </Loading>
  )
}

const groupReferences = (documents: LegalDocumentDto[] = []): (string | null)[] => {
  const groupReferences = documents.map((x) => x.groupReference)

  return [...new Set(groupReferences)]
}
